import {
  getMentionTextParts,
  isOnlyEmojis,
  replaceMentionPlaceholderWithNames,
  useTranslatedText,
} from '@gripp/shared-logic';
// import { useNavigation } from 'expo-router';
import { router } from 'expo-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, Platform, Pressable, StyleSheet, View } from 'react-native';
import Hyperlink from 'react-native-hyperlink';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { getImageThumbnailWidth } from '../media/getMediaThumbnailWidth';
import { MessageMedia } from './messageMedia';
import { messageStyles } from './styles';
import { MessageComponent, MessageComponentProps } from './types';

export const UserMessage: MessageComponent = (props: MessageComponentProps) => {
  const { t } = useTranslation();
  const { message, imageDimensions } = props;
  const [mediaWidth, setMediaWidth] = useState<number>();
  const { originalText, translatedText, needsTranslation } = useTranslatedText(
    'text',
    message
  );
  const [text, setText] = useState(
    needsTranslation ? translatedText : originalText
  );

  return (
    <View
      onLayout={(event) => {
        const { width } = event.nativeEvent.layout;
        const thumbnailWidth = getImageThumbnailWidth(
          width,
          imageDimensions.width ?? undefined
        );
        setMediaWidth(thumbnailWidth);
      }}
    >
      <View>
        <>
          {text && (
            <Hyperlink
              linkDefault={false}
              linkStyle={{ color: Colors.blue }}
              onPress={async (url, text) => {
                // see #1418, was having issue opening youtube urls on android
                // b/c canOpenURL was returning false
                // however, just opening the url without that check works.
                // posted to expo discord in case it's a bug:
                // https://discord.com/channels/695411232856997968/1287818543660011550
                try {
                  const parsed = new URL(url);

                  if (
                    parsed.hostname.includes('gripp.it') &&
                    Platform.OS !== 'web'
                  ) {
                    const path = parsed.pathname + parsed.search + parsed.hash;
                    router.push(path);
                    return;
                  }
                } catch (e) {
                  // Only catches URL parsing errors, not Linking errors
                }
                await Linking.openURL(url);
              }}
            >
              <Text
                style={[
                  props.textStyle,
                  isOnlyEmojis(text) ? styles.emoji : messageStyles.messageText,
                ]}
              >
                {getMentionTextParts(
                  replaceMentionPlaceholderWithNames(
                    text,
                    message.userMentions,
                    t('mentions.unknownUser')
                  )
                ).map((part, index) => {
                  if (part.isMention) {
                    return (
                      <Text key={index} style={styles.mentionHighlight}>
                        @{(part as any).name}
                      </Text>
                    );
                  } else {
                    return part.text;
                  }
                })}
              </Text>
            </Hyperlink>
          )}
          {needsTranslation && (
            <View style={styles.translateButton}>
              <Pressable
                onPress={() => {
                  if (text === originalText) {
                    setText(translatedText);
                  } else {
                    setText(originalText);
                  }
                }}
              >
                <Text style={styles.translationButton}>
                  {text === originalText
                    ? t('translation.seeTranslation')
                    : t('translation.seeOriginal')}
                </Text>
              </Pressable>
            </View>
          )}
          <MessageMedia
            media={props.message.media}
            displayWidth={mediaWidth}
            onImagePress={
              Platform.OS !== 'web' ? props.onImagePress : undefined
            }
          />
        </>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  mentionHighlight: {
    color: Colors.primaryDark,
    fontWeight: '400',
    flexWrap: 'wrap',
    flexShrink: 1,
  },
  translateButton: {
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  emoji: {
    fontSize: 48,
  },
  translationButton: {
    color: Colors.primaryDark,
    fontSize: 14,
    lineHeight: 16.8,
    marginTop: 8,
  },
});
